/**
 * BattleCode V2 — Rules Loader
 *
 * Phase 1: 数据层分离 — 将规则从 engine 硬编码中独立为 rules.json
 *
 * 设计模式与 map-utils.ts 的 MAP_REGISTRY 一致：
 *   - 编译时 import 所有内置 rules.json
 *   - 运行时 registerRuntimeRules() 支持自建关卡注入
 *   - loadRules(levelId) 查找顺序: runtime → static → fallback combat_1v1_standard
 *
 * Phase 1 只加载 + 校验数据，不接入引擎逻辑
 * Phase 2 起 GameMode 读取 rules.mode 决定加载哪个 Python API
 * Phase 3 起 Referee 读取 rules.referee 决定违规反馈策略
 * Phase 4 起 引擎读取 rules.win_conditions 支持 reach_zone
 */

import battleEasyRules from "../../../data/levels/_builtin/battle_easy/rules.json";
import rulesL0 from "../../../data/levels/_builtin/level_0_first_contact/rules.json";
import rulesL1 from "../../../data/levels/_builtin/level_1_open_duel/rules.json";
import rulesL2 from "../../../data/levels/_builtin/level_2_cover_fire/rules.json";
import rulesL3 from "../../../data/levels/_builtin/level_3_tactical_hold/rules.json";
import rulesL4 from "../../../data/levels/_builtin/level_4_lateral_strike/rules.json";
import rules3v3 from "../../../data/levels/_builtin/battle_3v3/rules.json";
import rules2v2 from "../../../data/levels/_builtin/showcase_2v2/rules.json";
import type { RulesConfig, GameModeType, RefereePersonality } from "./types";

// ============================================================
// Static Registry — 内置关卡的 rules.json
// ============================================================

const RULES_REGISTRY: Record<string, RulesConfig> = {
  battle_easy: battleEasyRules as RulesConfig,
  level_0_first_contact: rulesL0 as RulesConfig,
  level_1_open_duel: rulesL1 as RulesConfig,
  level_2_cover_fire: rulesL2 as RulesConfig,
  level_3_tactical_hold: rulesL3 as RulesConfig,
  level_4_lateral_strike: rulesL4 as RulesConfig,
  battle_3v3: rules3v3 as RulesConfig,
  showcase_2v2: rules2v2 as RulesConfig,
};

// ============================================================
// Runtime Registry — 自建关卡注入
// ============================================================

const RUNTIME_RULES = new Map<string, RulesConfig>();

/**
 * 注册运行时规则（自建关卡）。
 * LevelStore 加载自建关卡时调用此函数注入 rules.json。
 */
export function registerRuntimeRules(levelId: string, rules: RulesConfig): void {
  validateRules(rules, levelId);
  RUNTIME_RULES.set(levelId, rules);
}

// ============================================================
// 默认值
// ============================================================

/** 默认规则 ID（向后兼容，未指定 levelId 时用 battle_easy 的规则） */
export const DEFAULT_RULES_ID = "battle_easy";

/** 默认规则配置（fallback，永不返回 null） */
export const DEFAULT_RULES: RulesConfig = battleEasyRules as RulesConfig;

// ============================================================
// 公共 API
// ============================================================

/**
 * 根据 level ID 加载规则配置。
 * 查找顺序: runtime → static → fallback battle_easy 规则
 */
export function loadRules(levelId?: string): RulesConfig {
  const id = levelId ?? DEFAULT_RULES_ID;
  const rules = RUNTIME_RULES.get(id) ?? RULES_REGISTRY[id] ?? DEFAULT_RULES;
  return rules;
}

/**
 * 校验 rules.json 格式合法性。
 * Phase 1 只做结构校验，不做语义校验（如 API 名是否存在于 bot-api.py）。
 * 语义校验在 Phase 2 Worker init 时进行。
 */
export function validateRules(rules: RulesConfig, context?: string): void {
  const ctx = context ? ` (level: ${context})` : "";

  // rule_id
  if (!rules.rule_id || typeof rules.rule_id !== "string") {
    throw new Error(`[RulesLoader] Invalid rules.json${ctx}: rule_id must be a non-empty string`);
  }

  // mode
  const validModes: GameModeType[] = ["combat", "exploration", "puzzle", "teaching"];
  if (!validModes.includes(rules.mode)) {
    throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): mode must be one of ${validModes.join(", ")}`);
  }

  // win_conditions
  if (!Array.isArray(rules.win_conditions) || rules.win_conditions.length === 0) {
    throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): win_conditions must be a non-empty array`);
  }
  for (const wc of rules.win_conditions) {
    if (!wc.type) {
      throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): win_condition missing type`);
    }
    if (wc.type === "timeout" && (typeof wc.tick !== "number" || wc.tick <= 0)) {
      throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): timeout win_condition requires tick > 0`);
    }
    if (wc.type === "reach_zone" && (typeof wc.radius !== "number" || wc.radius <= 0)) {
      throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): reach_zone win_condition requires radius > 0`);
    }
  }

  // constraints
  if (!rules.constraints || typeof rules.constraints.max_units_per_team !== "number") {
    throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): constraints.max_units_per_team must be a number`);
  }

  // api_surface
  if (!rules.api_surface || !Array.isArray(rules.api_surface.base_apis)) {
    throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): api_surface.base_apis must be an array`);
  }

  // referee
  if (!rules.referee) {
    throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): referee config missing`);
  }
  const validPersonalities: RefereePersonality[] = ["transparent", "hint", "strict"];
  if (!validPersonalities.includes(rules.referee.personality)) {
    throw new Error(`[RulesLoader] Invalid rules.json${ctx} (rule_id: ${rules.rule_id}): referee.personality must be one of ${validPersonalities.join(", ")}`);
  }
}

/**
 * 清空运行时规则（测试用 + 关卡切换防残留）
 */
export function clearRuntimeRules(): void {
  RUNTIME_RULES.clear();
}

// ============================================================
// 启动时校验所有内置 rules.json
// ============================================================

for (const [levelId, rules] of Object.entries(RULES_REGISTRY)) {
  validateRules(rules, levelId);
}
